import { z } from 'zod'
import { DEFAULT_LOCALE, type Locale } from './i18n.ts'
import { getUnit } from './units.ts'

/**
 * Money is an integer count of minor units (paisa) plus a currency. There is no
 * float anywhere in the money path — see docs/02-architecture.md §3.
 */

export const CURRENCIES = ['NPR'] as const
export type Currency = (typeof CURRENCIES)[number]
export const currencySchema = z.enum(CURRENCIES)
export const DEFAULT_CURRENCY: Currency = 'NPR'

export const CURRENCY_META: Record<
  Currency,
  { minorPerMajor: number; symbol: string; symbolNe: string; code: string }
> = {
  NPR: { minorPerMajor: 100, symbol: 'Rs', symbolNe: 'रु', code: 'NPR' },
}

export function currencySymbol(
  currency: Currency = DEFAULT_CURRENCY,
  locale: Locale = DEFAULT_LOCALE,
): string {
  const meta = CURRENCY_META[currency]
  return locale === 'ne' ? meta.symbolNe : meta.symbol
}

/** Nepal groups digits the Indian way (12,34,567), which `en-IN` produces. */
const GROUPING_LOCALE = 'en-IN'

/** Above this, integer arithmetic on price x quantity risks leaving the safe range. */
export const MAX_MINOR = 1_000_000_000_000 // Rs 10 billion

export const minorAmountSchema = z
  .number()
  .int()
  .min(0)
  .max(MAX_MINOR, { message: 'Amount is too large' })

/**
 * Format paisa for display: 123456 -> "Rs 1,234.56", or "रु 1,234.56" in Nepali.
 *
 * Only the symbol changes. The digits and their grouping stay exactly as they are
 * in both languages — see the note in i18n.ts on why Devanagari numerals would make
 * a price harder to read for the audience, not easier.
 */
export function formatMoney(
  minor: number,
  currency: Currency = DEFAULT_CURRENCY,
  opts: { withSymbol?: boolean; decimals?: boolean; locale?: Locale } = {},
): string {
  const { withSymbol = true, decimals = true, locale = DEFAULT_LOCALE } = opts
  const meta = CURRENCY_META[currency]
  const negative = minor < 0
  const abs = Math.abs(minor)
  const whole = Math.trunc(abs / meta.minorPerMajor)
  const frac = abs % meta.minorPerMajor

  let body = whole.toLocaleString(GROUPING_LOCALE)
  if (decimals && frac !== 0) {
    body += `.${frac.toString().padStart(2, '0')}`
  }
  const sign = negative ? '-' : ''
  return withSymbol ? `${sign}${currencySymbol(currency, locale)} ${body}` : `${sign}${body}`
}

/** Parse "1234.56" into paisa. Returns null if unparseable or over-precise. */
export function parseMoney(input: string, currency: Currency = DEFAULT_CURRENCY): number | null {
  const cleaned = input.replace(/[\s,]/g, '').replace(/^Rs\.?/i, '')
  if (!/^-?\d*\.?\d*$/.test(cleaned) || cleaned === '' || cleaned === '.' || cleaned === '-') {
    return null
  }
  const negative = cleaned.startsWith('-')
  const [whole = '0', frac = ''] = cleaned.replace('-', '').split('.')
  if (frac.length > 2) return null
  const value = Number(whole) * CURRENCY_META[currency].minorPerMajor + Number(frac.padEnd(2, '0'))
  if (!Number.isSafeInteger(value) || value > MAX_MINOR) return null
  return negative ? -value : value
}

/**
 * unit price x quantity, where quantity is stored scaled by 10^unit.decimals.
 * Rounds half away from zero to the nearest paisa.
 */
export function lineTotal(unitPriceMinor: number, qty: number, unitCode: string): number {
  const scale = 10 ** getUnit(unitCode).decimals
  const product = unitPriceMinor * qty
  if (!Number.isSafeInteger(product)) {
    throw new RangeError('Line total exceeds safe integer range')
  }
  return Math.round(product / scale)
}

// ---------------------------------------------------------------------------
// Tax. Rates are basis points, so 13% VAT is 1300 — no float rates either.
// ---------------------------------------------------------------------------

export const DEFAULT_VAT_BPS = 1300
export const taxBpsSchema = z.number().int().min(0).max(10_000)

/** Tax on top of a tax-exclusive amount. */
export function taxOn(amountMinor: number, bps: number): number {
  return Math.round((amountMinor * bps) / 10_000)
}

/** Split a tax-inclusive amount into net + tax. */
export function splitInclusive(grossMinor: number, bps: number): { net: number; tax: number } {
  const net = Math.round((grossMinor * 10_000) / (10_000 + bps))
  return { net, tax: grossMinor - net }
}

/** Percentage of an amount, in basis points. Used for margins and discounts. */
export function applyBps(amountMinor: number, bps: number): number {
  return Math.round((amountMinor * bps) / 10_000)
}

/** Margin in basis points, given cost and sell. Returns null when cost is unknown. */
export function marginBps(sellMinor: number, costMinor: number | null): number | null {
  if (costMinor == null || sellMinor <= 0) return null
  return Math.round(((sellMinor - costMinor) / sellMinor) * 10_000)
}

export function formatBps(bps: number, decimals = 1): string {
  return `${(bps / 100).toFixed(decimals)}%`
}
